/**
 * SURFACE-COHERE (R14) — the ONE canonical float-distribution bin table for
 * Check #19. Built here in the engine from the same adjustable thresholds #17
 * and #18 use; the PDF helper and the dashboard both consume the engine's
 * emitted buckets + `distribution_summary` (Rule 12 — nothing else defines
 * bins or composes the sentence).
 *
 * Bins (N = nearCriticalFloatMax, E = excessiveFloatDays; N < E):
 *   negative  < 0
 *   zero      = 0
 *   nearCrit  1–N            (0 < d ≤ N)
 *   normal    (N+1)–E        (N < d ≤ E)
 *   excessive (E+1)–100      (E < d ≤ 100)     — dropped if E ≥ 100
 *   veryExcess 101–200        (100 < d ≤ 200)   — lower edge lifts to E+1 when E ≥ 100; dropped if E ≥ 200
 *   extreme   > max(200, E)   — the open bin
 *   noData    (float missing) — appended by the check, not float-day based
 *
 * The >E region is sub-split at the FIXED points 100 and 200 (today's PDF
 * behaviour). At defaults (N=10, E=44) the labels/predicates are byte-identical
 * to the shipped 7-bar PDF: 1–10 · 11–44 · 45–100 · 101–200 · 200+.
 *
 * Label convention (matches the shipped bytes): a closed bin (lo,hi] renders
 * "(lo+1)–hi"; the open `extreme` bin renders "L+" / "> L d" where L is its
 * exclusive lower edge (so defaults give "200+" / "> 200 d", NOT "201+").
 *
 * @module checks/floatBuckets
 */

const FIXED_SUBSPLIT = [100, 200];

/**
 * The canonical no-data bucket strings — the SINGLE source (was in the PDF
 * helper, which now re-exports these). The engine emits the noData bucket
 * carrying these directly, so both surfaces render them verbatim (Rule 12).
 */
export const NO_DATA_LABEL = 'No data';
export const NO_DATA_SUBLABEL = 'no float data';

/**
 * Map a bin id to its F-4 palette semantic (the healthy 11–44 band is the only
 * green; negative is critical red; zero / near-critical / every excessive
 * sub-band are warning amber; no-data is neutral grey). Kept id-keyed so both
 * renderers colour the engine buckets identically without a label match.
 * @param {object} palette  HISTOGRAM_PALETTE ({critical,warning,pass,grey})
 * @param {string} id
 * @returns {string}
 */
export function colorForBinId(palette, id) {
  if (id === 'negative') return palette.critical;
  if (id === 'normal') return palette.pass;
  if (id === 'noData') return palette.grey;
  return palette.warning; // zero, nearCrit, excessive, veryExcess, extreme
}

/**
 * Build the float-day bin table (7 bins; noData is appended by the check).
 * @param {number} nearCriticalFloatMax  N (default 10)
 * @param {number} excessiveFloatDays    E (default 44)
 * @returns {Array<{id:string,label:string,range:string,test:(d:number)=>boolean}>}
 */
export function buildFloatBinTable(nearCriticalFloatMax = 10, excessiveFloatDays = 44) {
  const N = nearCriticalFloatMax;
  const E = excessiveFloatDays;
  if (!(Number.isFinite(N) && Number.isFinite(E) && N < E)) {
    throw new Error(`float bins: nearCriticalFloatMax (${N}) must be a number < excessiveFloatDays (${E})`);
  }

  // Closed bin (loExcl, hiIncl]: displayed "(loExcl+1)–hiIncl".
  const closed = (id, loExcl, hiIncl) => ({
    id,
    label: `${loExcl + 1}–${hiIncl}`,
    range: `${loExcl + 1}–${hiIncl} d`,
    test: (d) => d > loExcl && d <= hiIncl,
  });

  const bins = [
    { id: 'negative', label: 'Negative', range: '< 0', test: (d) => d < 0 },
    { id: 'zero', label: 'Zero', range: '= 0', test: (d) => d === 0 },
    closed('nearCrit', 0, N),
    closed('normal', N, E),
  ];

  // >E region, sub-split at the surviving fixed points, then the open bin.
  const idForUpper = { 100: 'excessive', 200: 'veryExcess' };
  let prev = E;
  for (const cut of FIXED_SUBSPLIT) {
    if (cut > E) {
      bins.push(closed(idForUpper[cut], prev, cut));
      prev = cut;
    }
  }
  bins.push({
    id: 'extreme',
    label: `${prev}+`,
    range: `> ${prev} d`,
    test: (d) => d > prev,
  });

  return bins;
}

/**
 * Classify float-days into the bin table, returning counts + activityIds.
 * @param {Array<{floatDays:number, taskCode:string}>} entries
 * @param {number} N
 * @param {number} E
 * @returns {Array<{id,label,range,count,activityIds}>}
 */
export function classifyFloatDays(entries, N, E) {
  const bins = buildFloatBinTable(N, E).map((b) => ({
    id: b.id, label: b.label, range: b.range, test: b.test, count: 0, activityIds: [],
  }));
  for (const e of entries) {
    const d = Number(e.floatDays);
    if (!Number.isFinite(d)) continue;
    for (const b of bins) {
      if (b.test(d)) { b.count += 1; b.activityIds.push(e.taskCode); break; }
    }
  }
  return bins.map(({ test: _test, ...rest }) => rest);
}

/**
 * The one-sentence #19 interpretation — today's PDF logic, moved to the engine,
 * with N/E interpolated so the wording tracks the thresholds. At defaults
 * (E=44) the strings are byte-identical to the shipped PDF (L-X14 keeps 65%).
 *
 * @param {Array<{id:string,count:number}>} buckets  the 7-bin set (noData
 *   optional; excluded from the population by using `denominator`)
 * @param {number} denominator  canonical analysable-activity count
 * @param {number} excessiveFloatDays  E (for the band wording)
 * @returns {string}
 */
export function interpretDistribution(buckets, denominator, excessiveFloatDays = 44) {
  const E = excessiveFloatDays;
  const bucketSum = buckets.reduce((s, b) => s + (b.count || 0), 0);
  const base = (typeof denominator === 'number' && denominator > 0) ? denominator : bucketSum;
  if (base === 0) {
    return 'No float data available — activities may be missing scheduled dates.';
  }
  const of = (id) => (buckets.find((b) => b.id === id)?.count || 0);
  const neg = of('negative');
  const zero = of('zero');
  const nearCrit = of('nearCrit');
  const normal = of('normal');
  const high = of('excessive') + of('veryExcess') + of('extreme');
  const pct = (n) => Math.round((n / base) * 100);

  if (neg + zero > base * 0.25) {
    return `Distribution is critical-heavy: ${pct(neg + zero)}% of activities have zero or negative float — immediate risk to project finish.`;
  }
  if (high > base * 0.5) {
    return `Distribution is top-heavy: ${pct(high)}% of activities have >${E} days float, indicating possible missing logic.`;
  }
  if (nearCrit + normal > base * 0.5) {
    return `Distribution is balanced: ${pct(nearCrit + normal)}% of activities sit in the 1–${E} day band, consistent with a well-networked schedule.`;
  }
  return `Mixed distribution: ${pct(neg + zero)}% critical, ${pct(nearCrit + normal)}% near-critical/normal, ${pct(high)}% high-float.`;
}
